import {
  AddressModeType,
  AllowedQuery,
  AllowedQueryParam,
  CheckoutStatus,
  EnrichedItem,
  SessionIdState,
  SessionIdVisibility,
  iabFlowCheckoutLoadingStates,
} from './../../model/checkout'
import { CardBrand, Country, PaymentType, CashAppPayDetail, Money } from '@afterpay/types'
import { isMobile, ProvidedAddress, useGetSelectedTheme, useToast } from '@afterpaytouch/core'
import { createSelector } from '@reduxjs/toolkit'
import {
  CheckoutAppSelector,
  CheckoutState,
  useAppSelector,
  useRegistrationData,
  useCountry,
  useShippingAddress,
  useDonation,
  usePreferredPaymentMethod,
  useAppDispatch,
  useExperiment,
  useConsumerSelector,
  startCheckoutAndSelectPreferredPaymentMethod,
  getMarketingPreferences,
  getIdentities,
  useLogLoginAttemptEvent,
  useAppRouter,
  useUnverifiedMobileRoute,
  usePersonalizationEnabled,
  useIsTrustedCheckout,
  useIsPersistentAndOptedIn,
  ApplePayState,
  useCLRiskDeclineError,
  usePclPaymentOptions,
  useCohort,
  FeatureFlagsState,
  useIsApplePaylater,
  useExperimentTrigger,
  useIsConsumerLendingOrder,
  useConsumerFacilityCohort,
} from '../'
import { useFlag } from '@afterpaytouch/feature'
import {
  CheckoutConsumerNames,
  CheckoutError,
  EnrichedBankAccountDetails,
  EnrichedCardDetails,
  InitialData,
  lastPaymentDate,
  OAuth2faHeaders,
  PaymentInterval,
  Features,
  money,
  OtpLoginRouteAndTriggerResult,
  PaymentMethodType,
  isCountryNeedsBiometricVerification,
  errorsEligibleForIdVerification,
  terminalIdvErrors,
  LoginMethodTypes,
  SingleUseCardSubSource,
  DataVault,
  AppleRetailTrackingProps,
} from '../../model'
import {
  Checkout,
  CheckoutAddon,
  SubscriptionItem,
  AgreementType,
  Custom,
  CrossRegionData,
  CashAppObject,
  StartedCheckout,
  CheckoutPaymentSchedule,
  OfferDetail,
  DonationCampaignDetails,
  ErrorCode,
  IdentityCheckStatus,
  OrderSource,
  TermsDisclosureStatus,
  CheckoutMode,
  ConsumerLendingPromotion,
  AccountOpeningDisclosureConsent,
  ConsumerFeeItem,
  Item,
} from '@afterpaytouch/portal-api/types'
import { PreferredBankAccount } from '../../utils/bank-account'
import { PreferredCard } from '../../utils/credit-card'
import { isValidName } from '../../utils/validation'
import { isValidObject } from '../../utils/object'
import { Route } from '../../config/router'
import { isAfterpayInAppBrowser } from '../../utils/device'
import { useRouter } from 'next/router'
import { isNullOrUndefined, addMoneys, valueIsNotNullOrUndefined, isUndefinedOrEmpty } from '@afterpay/utils'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { TrackingEvent } from '../../model/monitoring'
import { checkoutService } from '@afterpaytouch/portal-api/consumer/checkout'
import { ItemType } from '@afterpaytouch/portal-api/consumer/checkout/types'
import { useTranslation } from '../../utils/convergence'
import { useState, useMemo, useCallback } from 'react'
import { app } from '../../env'
import { CheckoutErrorContext, createCheckoutErrorHandler } from '../../utils/checkoutErrorHandler'
import { isAfterpayFinanceFeeEligible } from '../../utils/payment-type'
import { browserRedirect } from '../../utils/redirects'

/* Selectors */

export const checkoutSelector: CheckoutAppSelector<CheckoutState> = (state) => state.checkout

export const checkoutStatusSelector: CheckoutAppSelector<CheckoutStatus> = createSelector(checkoutSelector, (state) => state?.status)

export const checkoutDataSelector: CheckoutAppSelector<Checkout | undefined> = createSelector(checkoutSelector, (state) => state?.checkout)

export const checkoutInitialDataSelector: CheckoutAppSelector<InitialData> = createSelector(checkoutSelector, (state) => state.initial)

export const startedCheckoutSelector: CheckoutAppSelector<StartedCheckout | undefined> = createSelector(checkoutSelector, (state) => state.started)

export const tokenSelector: CheckoutAppSelector<string> = createSelector(checkoutInitialDataSelector, (initial) => initial?.token ?? '')

export const merchantIdSelector: CheckoutAppSelector<number | undefined> = createSelector(checkoutDataSelector, (checkout) => checkout?.merchant.id)

export const merchantNameSelector: CheckoutAppSelector<string | undefined> = createSelector(checkoutDataSelector, (checkout) => checkout?.merchant.name)

export const sessionIdSelector: CheckoutAppSelector<SessionIdState> = createSelector(
  checkoutSelector,
  (state) => state?.sessionId ?? { visibility: SessionIdVisibility.HIDDEN }
)

export const merchantAvatarUrlSelector: CheckoutAppSelector<string> = createSelector(
  checkoutDataSelector,
  (checkout) => checkout?.merchant.merchantAvatarUrl ?? ''
)

export const merchantDisplayNameSelector: CheckoutAppSelector<string | undefined> = createSelector(
  checkoutDataSelector,
  (checkout) => checkout?.merchant.merchantDisplayName
)

export const cashAppObjectSelector: CheckoutAppSelector<CashAppObject | undefined> = createSelector(checkoutDataSelector, (checkout) => checkout?.cashApp)

export const cashAppConsumerNameSelector: CheckoutAppSelector<string> = createSelector(checkoutDataSelector, (checkout) => {
  const givenNames = checkout?.cashApp?.details?.givenNames ?? ''
  const surname = checkout?.cashApp?.details?.surname ?? ''
  if (givenNames.length > 0 && surname.length > 0) {
    return `${givenNames} ${surname}`
  }
  return `${givenNames}${surname}`
})

export const shippingOptionIdentifierSelector: CheckoutAppSelector<string | undefined> = createSelector(
  checkoutDataSelector,
  // @ts-expect-error: OPERATION BLEED STOPPER
  (checkout) => checkout.shippingOptionIdentifier
)

export const traceIdSelector: CheckoutAppSelector<string> = createSelector(startedCheckoutSelector, (started) => started?.traceId ?? '')

export const consumerAmountSelector: CheckoutAppSelector<Money | undefined> = createSelector(startedCheckoutSelector, (state) => state?.consumerAmount)

export const merchantAmountSelector: CheckoutAppSelector<Money | undefined> = createSelector(startedCheckoutSelector, (state) => state?.merchantAmount)

export const consumerAmountWithFeesSelector: CheckoutAppSelector<Money | undefined> = createSelector(
  startedCheckoutSelector,
  (state) => state?.consumerAmountWithFees
)

export const savedCardsSelector: CheckoutAppSelector<EnrichedCardDetails[]> = createSelector(checkoutSelector, (state) => state.cards)

export const cashAppPaysSelector: CheckoutAppSelector<CashAppPayDetail[]> = createSelector(checkoutSelector, (state) => state.cashAppPays)

export const enabledCardTypesSelector: CheckoutAppSelector<CardBrand[]> = createSelector(
  startedCheckoutSelector,
  (started) => started?.enabledCardTypes ?? [CardBrand.VISA, CardBrand.MASTERCARD]
)

export const savedBankAccountsSelector: CheckoutAppSelector<EnrichedBankAccountDetails[] | undefined> = createSelector(
  checkoutSelector,
  (state) => state.bankAccounts
)

export const lastPaymentDateSelector: CheckoutAppSelector<Date | null> = createSelector(startedCheckoutSelector, (started) => {
  const schedules = started?.paymentSchedules ?? []
  return schedules.length === 0 ? null : lastPaymentDate(schedules)
})

export const donationCampaignDetailsSelector: CheckoutAppSelector<DonationCampaignDetails | null> = createSelector(
  startedCheckoutSelector,
  (started) => started?.donationCampaignDetails ?? null
)

export const checkoutModeSelector: CheckoutAppSelector<CheckoutMode> = createSelector(
  checkoutDataSelector,
  (state) => state?.checkoutMode ?? CheckoutMode.STANDARD
)

export const preferredCardSelector: CheckoutAppSelector<PreferredCard | undefined> = createSelector(checkoutSelector, (state) => state.preferredCard)

export const preferredBankAccountSelector: CheckoutAppSelector<PreferredBankAccount | undefined> = createSelector(
  checkoutSelector,
  (state) => state.preferredBankAccount
)

export const checkoutErrorSelector: CheckoutAppSelector<CheckoutError | undefined> = createSelector(checkoutSelector, (state) => state?.error)

export const paymentIntervalSelector: CheckoutAppSelector<PaymentInterval> = createSelector(
  startedCheckoutSelector,
  // @ts-expect-error: OPERATION BLEED STOPPER
  (state) => PaymentInterval[state?.paymentInterval] ?? PaymentInterval.FORTNIGHTLY
)

export const offersSelector: CheckoutAppSelector<OfferDetail[]> = createSelector(startedCheckoutSelector, (state) => state?.offers ?? [])

export const installmentAmountSelector: CheckoutAppSelector<Money | undefined> = createSelector(startedCheckoutSelector, (state) => state?.installmentAmount)

export const amountDueTodaySelector: CheckoutAppSelector<Money | undefined> = createSelector(startedCheckoutSelector, (state) => state?.amountDueToday)

export const consumerLendingEligibleSelector: CheckoutAppSelector<boolean> = createSelector(
  startedCheckoutSelector,
  (state) => state?.offers?.some((offer) => offer.paymentType === PaymentType.PCL) ?? false
)

export const creditCheckConsentSelector: CheckoutAppSelector<boolean | undefined> = createSelector(
  startedCheckoutSelector,
  (state) => state?.creditCheckConsent
)

export const otbAmountSelector: CheckoutAppSelector<Money | undefined> = createSelector(startedCheckoutSelector, (state) => state?.otbAmount)

export const isExpressSelector: CheckoutAppSelector<boolean> = createSelector(checkoutModeSelector, (mode) => mode === CheckoutMode.EXPRESS)

export const isShippingOptionRequiredSelector: CheckoutAppSelector<boolean> = createSelector(
  checkoutInitialDataSelector,
  (initial) => initial?.shippingOptionRequired
)

export const isCashAppSelector: CheckoutAppSelector<boolean> = createSelector(checkoutDataSelector, (state) => !(state?.cashApp == null))

export const checkoutCountryCodeSelector: CheckoutAppSelector<string | undefined> = createSelector(checkoutDataSelector, (state) => state?.countryCode)

export const hasBillingAgreementSelector: CheckoutAppSelector<boolean> = createSelector(
  checkoutDataSelector,
  // @ts-expect-error: OPERATION BLEED STOPPER
  (state) => state?.agreements?.filter((agreement) => agreement.type === AgreementType.BILLING).length > 0
)

export const isBillingAgreementApprovalProcessSelector: CheckoutAppSelector<boolean> = createSelector(
  checkoutDataSelector,
  (state) => state?.billingAgreementApprovalProcess ?? false
)

export const isSingleUseCardSelector: CheckoutAppSelector<boolean> = createSelector(
  checkoutDataSelector,
  (state) => state?.merchant.source === OrderSource.SINGLE_USE_CARD
)

export const singleUseCardSubSourceSelector: CheckoutAppSelector<SingleUseCardSubSource | undefined> = createSelector(
  checkoutDataSelector,
  (state) => state?.merchant.singleUseCardSubSource
)

export const redirectUrlSelector: CheckoutAppSelector<string> = createSelector(checkoutSelector, ({ checkout, confirmed }) => {
  if (confirmed !== undefined) {
    return confirmed.callbackBaseUrl ?? checkout?.merchant.callbackUrl ?? ''
  }
  return checkout?.merchant.cancelCallbackUrl ?? ''
})

export const isPickupSelector: CheckoutAppSelector<boolean> = createSelector(checkoutSelector, (state) => state?.initial?.pickup)

export const checkoutConsumerNamesSelector: CheckoutAppSelector<CheckoutConsumerNames> = createSelector(checkoutDataSelector, (checkout) => {
  return {
    givenNames: checkout?.consumer?.givenNames ?? '',
    surname: checkout?.consumer?.surname ?? '',
  }
})

export const checkoutConsumerPhoneNumberSelector: CheckoutAppSelector<string> = createSelector(
  checkoutDataSelector,
  (checkout) => checkout?.consumer?.phoneNumber ?? ''
)

export const checkoutConsumerEmailSelector: CheckoutAppSelector<string> = createSelector(checkoutDataSelector, (checkout) => checkout?.consumer?.email ?? '')

export const checkoutAccountOpeningDisclosureSelector: CheckoutAppSelector<AccountOpeningDisclosureConsent | undefined> = createSelector(
  startedCheckoutSelector,
  (checkout) => checkout?.accountOpeningDisclosureConsent
)

export const confirmTermsViewedSelector: CheckoutAppSelector<string | null> = createSelector(checkoutSelector, (state) => state.confirmTermsViewed)

// @ts-expect-error: OPERATION BLEED STOPPER
export const checkoutCustomObjectSelector: CheckoutAppSelector<Custom> = createSelector(checkoutDataSelector, (checkout) => checkout?.custom)

export const oAuth2faHeaders: CheckoutAppSelector<OAuth2faHeaders> = createSelector(checkoutCustomObjectSelector, (custom) =>
  getOAuthLoginHeadersFromOrder(custom)
)

// @ts-expect-error: OPERATION BLEED STOPPER
export const checkoutAddressSelector: CheckoutAppSelector<ProvidedAddress[]> = createSelector(checkoutDataSelector, (checkout) => {
  const primaryAddress = {
    address1: checkout?.billingAddress?.address1,
    address2: checkout?.billingAddress?.address2,
    area2: checkout?.billingAddress?.area2 ?? '',
    postcode: checkout?.billingAddress?.postcode,
    state: checkout?.billingAddress?.state,
    suburb: checkout?.billingAddress?.suburb,
  }
  const secondaryAddress = {
    address1: checkout?.shippingAddress?.address1,
    address2: checkout?.shippingAddress?.address2,
    area2: checkout?.shippingAddress?.area2 ?? '',
    postcode: checkout?.shippingAddress?.postcode,
    state: checkout?.shippingAddress?.state,
    suburb: checkout?.shippingAddress?.suburb,
  }
  const primaryAddressValid = isValidObject(checkout?.billingAddress)
  const secondaryAddressValid = isValidObject(checkout?.shippingAddress)

  if (!primaryAddressValid) {
    return secondaryAddressValid ? [secondaryAddress] : []
  }

  return secondaryAddressValid ? [primaryAddress, secondaryAddress] : [primaryAddress]
})

// @ts-expect-error: OPERATION BLEED STOPPER
export const exchangeRateSelector: CheckoutAppSelector<string> = createSelector(startedCheckoutSelector, (started) => started?.exchangeRate)

// @ts-expect-error: OPERATION BLEED STOPPER
export const checkoutShippingCountrySelector: CheckoutAppSelector<string> = createSelector(
  checkoutDataSelector,
  (checkout) => checkout?.shippingOptions?.shippingCountry
)

export const internationalSupportedCountriesSelector: CheckoutAppSelector<string[]> = createSelector(
  checkoutDataSelector,
  (checkout) => checkout?.shippingOptions?.supportedShippingCountries ?? []
)

// @ts-expect-error: OPERATION BLEED STOPPER
export const crossRegionUserSignInSelector: CheckoutAppSelector<CrossRegionData> = createSelector(
  checkoutDataSelector,
  (checkout) => checkout?.crossRegionUserSignIn
)

export const creditCheckAgreeSelector: CheckoutAppSelector<boolean> = createSelector(checkoutSelector, (state) => state.creditCheckAgree)

export const termsDisclosureStatusSelector: CheckoutAppSelector<TermsDisclosureStatus | undefined> = createSelector(
  startedCheckoutSelector,
  (state) => state?.termsDisclosureStatus
)

export const downPaymentAmountSelector: CheckoutAppSelector<number> = createSelector(startedCheckoutSelector, (started) =>
  parseFloat(started?.downPaymentAmount?.amount ?? '0')
)

export const termsDisclosureAgreeSelector: CheckoutAppSelector<boolean | undefined> = createSelector(checkoutSelector, (state) => state.termsDisclosureAgree)

export const allowedQuerySelector: CheckoutAppSelector<AllowedQuery> = createSelector(checkoutSelector, (state) => state.allowedQueryParam)

// TODO: fix OrderTransactionSource to differentiate AP button and SUP
// check apt-api-plus repo SingleUseCardsResourceManager
export const buttonMerchantSelector: CheckoutAppSelector<boolean> = createSelector(
  checkoutDataSelector,
  (state) => state?.merchant.source === OrderSource.SINGLE_USE_CARD
)

export const isEnabledPBIAutoPaySelector: CheckoutAppSelector<boolean> = createSelector(checkoutSelector, (state) => state.isEnabledAutoPay)

export const checkoutAddonSelector: CheckoutAppSelector<CheckoutAddon[]> = createSelector(checkoutDataSelector, (state) => state?.addOns ?? [])

export const consumerLendingPromotionSelector: CheckoutAppSelector<ConsumerLendingPromotion | null> = createSelector(
  startedCheckoutSelector,
  (started) => started?.consumerLendingPromotion ?? null
)

export const featureFlagsSelector: CheckoutAppSelector<FeatureFlagsState> = (state) => state.featureFlags

export const offerRanksSelector: CheckoutAppSelector<PaymentType[]> = createSelector(startedCheckoutSelector, (state) => state?.offerRanks ?? [])

export const integrationNextRouteSelector: CheckoutAppSelector<Route> = createSelector(
  checkoutSelector,
  (state) => state.integrationContext?.nextRoute ?? Route.INDEX
)

export const itemsVaultSelector: CheckoutAppSelector<DataVault<EnrichedItem>> = createSelector(
  checkoutSelector,
  (state) => state.items ?? { ids: [], data: {} }
)

export const itemByIdSelector = (id: string): CheckoutAppSelector<EnrichedItem | undefined> => createSelector(itemsVaultSelector, (vault) => vault.data[id])

/* React Hooks */

export const useCheckoutState = (): CheckoutState => useAppSelector(checkoutSelector)

export const useDownPaymentAmount = (): number => useAppSelector(downPaymentAmountSelector)

export const useCheckoutStatus = (): CheckoutStatus => useAppSelector(checkoutStatusSelector)

export const useInitialData = (): InitialData => useAppSelector(checkoutInitialDataSelector)

export const useCheckoutData = (): Checkout | undefined => useAppSelector(checkoutDataSelector)

export const useCheckoutToken = (): string => useAppSelector(tokenSelector)

export const useIsEmptyCheckoutToken = (): boolean => {
  const token = useCheckoutToken()
  return token.trim() === ''
}

export const useTraceId = (): string => useAppSelector(traceIdSelector)

export const useCheckoutCountryCode = (): string | undefined => useAppSelector(checkoutCountryCodeSelector)

export const useMerchantId = (): number | undefined => useAppSelector(merchantIdSelector)

export const useMerchantName = (): string | undefined => useAppSelector(merchantNameSelector)

export const useMerchantDisplayName = (): string | undefined => useAppSelector(merchantDisplayNameSelector)

export const useMerchantAvatarUrl = (): string => useAppSelector(merchantAvatarUrlSelector)

export const useShippingOptionIdentifier = (): string | undefined => useAppSelector(shippingOptionIdentifierSelector)

export const useSavedCards = (): EnrichedCardDetails[] => useAppSelector(savedCardsSelector)

export const useSavedBankAccounts = (): EnrichedBankAccountDetails[] | undefined => useAppSelector(savedBankAccountsSelector)

export const useCashAppPay = (): CashAppPayDetail[] => useAppSelector(cashAppPaysSelector)

export const useCashAppConsumerName = (): string => useAppSelector(cashAppConsumerNameSelector)

export const useEnabledCardTypes = (): CardBrand[] => useAppSelector(enabledCardTypesSelector)

export const useLastPaymentDate = (): Date | null => useAppSelector(lastPaymentDateSelector)

export const useCheckoutError = (): CheckoutError | undefined => useAppSelector(checkoutErrorSelector)

export const usePaymentInterval = (): PaymentInterval => useAppSelector(paymentIntervalSelector)

export const useConsumerAmount = (): Money | undefined => useAppSelector(consumerAmountSelector)

export const useMerchantAmount = (): Money | undefined => useAppSelector(merchantAmountSelector)

export const useConsumerAmountWithFees = (): Money | undefined => useAppSelector(consumerAmountWithFeesSelector)

export const useOfferDetails = (): OfferDetail[] => useAppSelector(offersSelector)

export const useOfferDetailPCL = (): OfferDetail | undefined => {
  const offers = useAppSelector(offersSelector)

  return offers.find((od) => od.paymentType === PaymentType.PCL)
}

export const useOfferDetailPBI = (): OfferDetail | undefined => {
  const offers = useAppSelector(offersSelector)
  return offers.find((od) => od.paymentType === PaymentType.PBI)
}

export const useInstallmentAmount = (): Money | undefined => useAppSelector(installmentAmountSelector)

export const useMerchantAvatarUrlWithFallback = (fallbackUrl = ''): string => {
  const avatarUrl = useMerchantAvatarUrl()
  const hasAvatarUrl = !isUndefinedOrEmpty(avatarUrl)
  if (hasAvatarUrl) {
    if (avatarUrl.startsWith('https:')) {
      return avatarUrl
    } else {
      return `https:${avatarUrl}`
    }
  }

  return fallbackUrl
}

export const useConsumerLendingEligible = (): boolean => {
  const consumerLendingOffersExist = useAppSelector(consumerLendingEligibleSelector)
  const checkoutMode = useCheckoutMode()
  const initial = useInitialData()
  const isDigitalGood = useIsDigitalGoodForCLExpress()

  if (consumerLendingOffersExist && checkoutMode === CheckoutMode.EXPRESS) {
    if (initial.addressMode === AddressModeType.NO_ADDRESS) {
      return isDigitalGood
    }
    return initial.buyNow && initial.shippingOptionRequired
  }
  return consumerLendingOffersExist
}

export const useIsDigitalGoodForCLExpress = (): boolean => {
  const initial = useInitialData()
  const noAddressModeFlag = initial.buyNow && !initial.shippingOptionRequired
  return initial.addressMode === AddressModeType.NO_ADDRESS && noAddressModeFlag
}

export const useGetCreditCheckConsent = (): boolean | undefined => useAppSelector(creditCheckConsentSelector)

export const useGetTermsDisclosureStatus = (): TermsDisclosureStatus | undefined => useAppSelector(termsDisclosureStatusSelector)

export const useOtbAmount = (): Money | undefined => useAppSelector(otbAmountSelector)

export const useCheckoutMode = (): CheckoutMode => useAppSelector(checkoutModeSelector)

export const useIsExpress = (): boolean => useAppSelector(isExpressSelector)

export const useIsShippingOptionRequired = (): boolean => useAppSelector(isShippingOptionRequiredSelector)

export const useDonationCampaignDetails = (): DonationCampaignDetails | null => useAppSelector(donationCampaignDetailsSelector)

export const useIsCashApp = (): boolean => useAppSelector(isCashAppSelector)

export const useHasBillingAgreements = (): boolean => {
  return useAppSelector(hasBillingAgreementSelector)
}

export const useIsBillingAgreementApprovalProcess = (): boolean => {
  return useAppSelector(isBillingAgreementApprovalProcessSelector)
}

export const useIsSingleUseCard = (): boolean => useAppSelector(isSingleUseCardSelector)

export const useSingleUseCardSubSource = (): SingleUseCardSubSource | undefined => useAppSelector(singleUseCardSubSourceSelector)

export const useIsAfterpayFinanceFeeEligible = (): boolean => {
  const consumerFees = useConsumerFees()
  return isAfterpayFinanceFeeEligible(consumerFees)
}

export const useIsSupOrder = (): boolean => {
  const singleUseCardSubSource = useSingleUseCardSubSource()
  const isSingleUseCard = useIsSingleUseCard()

  return singleUseCardSubSource === SingleUseCardSubSource.SINGLE_USE_PAYMENT && isSingleUseCard
}

export const useIsEstimatedPPA = (): boolean => {
  const merchantId = useMerchantId()
  const isSupOrder = useIsSupOrder()
  const { MERCHANT_ESTIMATE_PPA_IDS } = app
  const merchantEstimatePpaIds = MERCHANT_ESTIMATE_PPA_IDS?.split(',').map(Number) ?? []

  return (merchantId !== null && merchantId !== undefined && merchantEstimatePpaIds.includes(merchantId)) || isSupOrder
}

export const useActiveCashAppPay = (): CashAppPayDetail[] => {
  const cashAppPays = useCashAppPay()
  return cashAppPays.filter((cashAppPay) => cashAppPay.status === 'ACTIVE')
}

export const useHasSavedPaymentMethods = (): boolean => {
  const cards = useSavedCards()
  const bankAccounts = useSavedBankAccounts()
  const cashAppPay = useCashAppPay()
  return cards.length > 0 || cashAppPay.length > 0 || (bankAccounts?.length ?? 0) > 0
}

export const useRedirectUrl = (): string => useAppSelector(redirectUrlSelector)

export const useIsPickup = (): boolean => useAppSelector(isPickupSelector)

export const useItemsAndSubscriptions = (): Array<Item | SubscriptionItem> => {
  const state = useCheckoutState()

  const items = state?.checkout?.items ?? []
  const subscriptionItems = state?.checkout?.subscriptions ?? []

  return [...items, ...subscriptionItems]
}

export const useConsumerFees = (): ConsumerFeeItem[] => {
  const state = useCheckoutState()
  const consumerFees = state?.consumerFees ?? []
  return consumerFees
}

export const useSUPFinanceFee = (): ConsumerFeeItem | undefined => useConsumerFees().find((fee) => fee.type === 'CONSUMER_FINANCE_FEE' && fee.variableAmount)

export const useCheckoutConsumerNames = (): CheckoutConsumerNames => useAppSelector(checkoutConsumerNamesSelector)

export const useCheckoutConsumerPhoneNumber = (): string => useAppSelector(checkoutConsumerPhoneNumberSelector)
export const useCheckoutConsumerEmail = (): string => useAppSelector(checkoutConsumerEmailSelector)

export const useIsDeferredShipping = (): boolean => {
  const isExpress = useIsExpress()
  const shippingOptionRequired = useIsShippingOptionRequired()

  return isExpress && !shippingOptionRequired
}

const getOAuthLoginHeadersFromOrder = (custom?: Custom): OAuth2faHeaders => {
  const headers = {}

  if (valueIsNotNullOrUndefined(custom)) {
    const deviceInfo = custom.device

    if (valueIsNotNullOrUndefined(deviceInfo)) {
      headers['oauth-user-agent'] = deviceInfo.userAgent
      headers['oauth-device-fingerprint'] = deviceInfo.fingerPrint
      headers['oauth-client-ip'] = deviceInfo.ip
    }

    headers['oauth-login-time'] = custom.loginTimeAt
  }

  return headers
}

export const useCheckoutRegistrationAddress = (): ProvidedAddress[] | ProvidedAddress => {
  const registrationData = useRegistrationData()
  const checkoutAddress = useAppSelector(checkoutAddressSelector)

  // PAYL-16943: after navigating to the sign up page, `registrationData` already has the `email` value
  if (registrationData?.address1 === undefined) {
    return checkoutAddress
  }

  return {
    address1: registrationData?.address1,
    address2: registrationData?.address2 ?? '',
    area2: registrationData?.area2 ?? '',
    postcode: registrationData?.postcode,
    state: registrationData?.state ?? '',
    suburb: registrationData?.suburb,
  }
}

export const useEnableEditName = (): boolean => {
  const { givenNames, surname } = useCheckoutConsumerNames()
  if (useIsExpress()) {
    return true
  }
  return !(isValidName({ name: givenNames }) && isValidName({ name: surname }))
}

export const useCrossRegionUserSignIn = (): { isCrossRegion: boolean; crossRegionData } => {
  const crossRegionData = useAppSelector(crossRegionUserSignInSelector)
  if (valueIsNotNullOrUndefined(crossRegionData)) {
    return { isCrossRegion: true, crossRegionData }
  }
  return { isCrossRegion: false, crossRegionData: undefined }
}

export const useCrossBorderTrade = (): { isCrossBorder: boolean; exchangeRate?: string } => {
  const exchangeRate = useAppSelector(exchangeRateSelector)
  if (exchangeRate !== undefined && exchangeRate !== null && exchangeRate !== '') {
    return { isCrossBorder: true, exchangeRate }
  }

  return { isCrossBorder: false, exchangeRate: undefined }
}

export const usePreferredCard = (): PreferredCard | undefined => useAppSelector(preferredCardSelector)

export const useIsMCP = (): boolean => {
  const checkout = useCheckoutData()
  return checkout?.merchant.merchantCurrencyCode !== undefined && checkout?.merchant.merchantCurrencyCode !== ''
    ? checkout?.merchant.merchantCurrencyCode !== checkout?.amount.currency
    : false
}

export const usePreferredBankAccount = (): PreferredBankAccount | undefined => useAppSelector(preferredBankAccountSelector)

export const useShouldUseBankAccount = (): boolean => {
  const preferredPaymentMethod = usePreferredPaymentMethod()
  const preferredBankAccount = usePreferredBankAccount()
  const account = preferredBankAccount?.account ?? null
  return preferredPaymentMethod === PaymentMethodType.ACH_BANK_ACCOUNT && account !== null && account.fraudCheckAchDisallowed !== true
}

export const useIsBankAccountSelectionFailed = (): boolean => {
  const preferredPaymentMethod = usePreferredPaymentMethod()
  const preferredBankAccount = usePreferredBankAccount()
  const account = preferredBankAccount?.account ?? null
  return preferredPaymentMethod === PaymentMethodType.ACH_BANK_ACCOUNT && account !== null && account.fraudCheckAchDisallowed === true
}

export const useInternationalShippingCountry = (): string => useAppSelector(checkoutShippingCountrySelector)
export const useInternationalSupportedShippingCountries = (): string[] => useAppSelector(internationalSupportedCountriesSelector)

export const useIsIntlShippingSupportConsumerCountry = (): boolean => {
  const checkout = useCheckoutData()
  const country = useCountry()
  return checkout?.shippingOptions?.supportedShippingCountries.includes(country) ?? false
}

export const useInternationalShippingEnabled = (): boolean => {
  const isInternationalShippingEnabled = useFlag(Features.INTERNATIONAL_SHIPPING_ENABLED)
  const supportedShippingCountries = useInternationalSupportedShippingCountries()
  const hasShippingOptionCountries = supportedShippingCountries?.length > 0
  return hasShippingOptionCountries && isInternationalShippingEnabled
}

export const useIsPreferredShippingCountrySupported = (): boolean => {
  const supportedShippingCountries = useInternationalSupportedShippingCountries()
  const { allAddresses } = useShippingAddress()
  if (allAddresses?.length === 0) {
    return false
  }
  return supportedShippingCountries.includes(allAddresses[0].countryCode)
}

export const useIsChosenShippingCountrySupported = (): boolean => {
  const { chosenAddress } = useShippingAddress()
  const supportedShippingCountries = useInternationalSupportedShippingCountries()
  return chosenAddress !== undefined ? supportedShippingCountries.includes(chosenAddress.countryCode) : false
}

export const useCalculatedPaymentSchedules = (): CheckoutPaymentSchedule[] => {
  const { started } = useCheckoutState()
  const { donationAmount } = useDonation()
  let paymentSchedules = started?.paymentSchedules ?? []
  if (valueIsNotNullOrUndefined(started?.promotion)) {
    paymentSchedules = started.promotion.orderDiscountedPaymentSchedules.map((schedule) => {
      return {
        ...schedule,
        amount: schedule.owedAfterDiscount ?? money('0.00', schedule.amount.currency),
      }
    })
  }

  if (donationAmount > 0) {
    paymentSchedules = paymentSchedules?.map((schedule, idx) => {
      if (idx === 0) {
        const { amount, currency } = schedule.amount
        return {
          ...schedule,
          amount: money(`${parseFloat(amount) + donationAmount}`, currency),
        }
      }
      return schedule
    })
  }

  return paymentSchedules
}
export const useIsOtpAsPrimaryLoginMethodEnabled = (): boolean => {
  const otpAsPrimaryLoginMethodEnabled = useFlag(Features.OTP_AS_PRIMARY_LOGIN_METHOD)
  return otpAsPrimaryLoginMethodEnabled
}

export const useOtpLoginRouteAndTrigger = (): OtpLoginRouteAndTriggerResult => {
  const isOTPAsPrimaryLoginMethodEnabled = useIsOtpAsPrimaryLoginMethodEnabled()
  const isMobileDevice = isMobile()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const isApplepayLaterFlow = useIsApplePaylater()

  return (isTrustedSession, isBiometricsRegisteredAndDeviceEligible) => {
    const eligibleCondition = !isBiometricsRegisteredAndDeviceEligible && isMobileDevice && isTrustedSession && !isApplepayLaterFlow
    const defaultRedirectToOTP = eligibleCondition && isOTPAsPrimaryLoginMethodEnabled
    logEvent(TrackingEvent.OTP_LOGIN_ROUTE_AND_TRIGGER, {
      isMobileDevice,
      isTrustedSession,
      biometricsRegisteredAndDeviceEligible: isBiometricsRegisteredAndDeviceEligible,
      defaultRedirectToOTP,
    })

    return defaultRedirectToOTP ? Route.ONE_TIME_PASSWORD : Route.ENTER_PASSWORD
  }
}

export const useMerchantDisplayNameOrName = (): string => {
  const merchantName = useMerchantName() ?? ''
  const merchantDisplayName = useMerchantDisplayName() ?? ''
  if (merchantDisplayName.length > 0) {
    return merchantDisplayName
  }
  return merchantName
}

export const useCallbackUrlQueryArgs = (): string => {
  const { checkout, confirmed } = useCheckoutState()
  return confirmed?.callbackUrlQueryArgs ?? checkout?.merchant.callbackUrlQueryArgs ?? ''
}

export const useIsAfterpayAppGiftCardFlow = (): boolean => {
  const isAfterpayInAppSession = isAfterpayInAppBrowser()
  const merchantId = useMerchantId()

  const giftCardFlowEnabledMerchants = useFlag<number[]>(Features.CHECKOUT_GIFT_CARD_MERCHANTS_FLOW, [])
  const giftCardFlowEnabled = merchantId !== undefined && giftCardFlowEnabledMerchants.includes(merchantId)

  return isAfterpayInAppSession && giftCardFlowEnabled
}

export const useIsOnIabCheckoutInit = (): boolean => {
  const checkoutStatus = useCheckoutStatus()
  return iabFlowCheckoutLoadingStates.includes(checkoutStatus)
}

// in in-app browser sessions, show a full loading page when transitioning from mobile -> checkout
export const useShowLoaderOnly = (): boolean => {
  const { pathname } = useRouter()
  const isAfterpayInAppSession = isAfterpayInAppBrowser()
  const isOnIabCheckoutInit = useIsOnIabCheckoutInit()

  const eligibleRoutes = [Route.LOGGED_IN, Route.INDEX]

  const isEligibleRoute = eligibleRoutes.includes(pathname as Route)

  return isAfterpayInAppSession && (isOnIabCheckoutInit || isEligibleRoute)
}

export const useAmountDueToday = (): Money | undefined => {
  const { started } = useCheckoutState()

  if (isNullOrUndefined(started)) {
    return
  }
  const initialAmount = started.amountDueToday
  const moneys: Money[] = [initialAmount]
  const currency = started?.amountDueToday.currency

  try {
    const totalAmount = addMoneys({ moneys })
    return money(totalAmount, currency)
  } catch (e) {
    console.error(e)
  }
}

export const useAllowedQuery = (): AllowedQuery => {
  const allowedQuery = useAppSelector(allowedQuerySelector) ?? {}

  const parsedAllowedQuery = {}

  Object.keys(AllowedQueryParam).forEach((param) => {
    parsedAllowedQuery[param] = allowedQuery?.[param] ?? false
  })

  return parsedAllowedQuery as AllowedQuery
}

export interface UseStartCheckoutOptions {
  returnApplePayForPayLater?: boolean
}

export interface UseStartCheckoutResponse {
  doStartCheckoutAndHandleError: () => Promise<void>
}

export const useStartCheckout = (options?: UseStartCheckoutOptions): UseStartCheckoutResponse => {
  const dispatch = useAppDispatch()
  const country = useCountry()
  const logLoginAttemptEvent = useLogLoginAttemptEvent({ is2fa: false })
  const router = useAppRouter()

  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const unverifiedMobileRoute = useUnverifiedMobileRoute()
  const toast = useToast()
  const { t } = useTranslation()
  const { hasPassword } = useConsumerSelector()
  const enableRedirectionIncompleteConsumerProfile = useFlag<boolean>(Features.ENABLE_REDIRECTION_INCOMPLETE_CONSUMER_PROFILE, false)
  const isPersonalizationEnabled = usePersonalizationEnabled()
  const isTrustedCheckout = useIsTrustedCheckout()
  const isPersistentAndOptedIn = useIsPersistentAndOptedIn()
  const loginMethod: LoginMethodTypes = isTrustedCheckout ? 'Trusted Checkout (OAuth)' : 'Web Persistent'
  const [hasRetriedStartCheckout, setHasRetriedStartCheckout] = useState(false)
  const isEnabledForMPANApplePayFlow = useFlag<boolean>(Features.ENABLE_MPAN_APPLE_PAY_FLOW, false)
  const isFetchApplePayRepaymentEnabled = useFlag<boolean>(Features.ENABLE_FETCH_APPLE_PAYLATER_APPLE_PAY_REPAYMENT, false)
  const enableIncompleteProfileErrorRetry = useFlag<boolean>(Features.INCOMPLETE_PROFILE_ERROR_RETRY, false)
  const theme = useGetSelectedTheme()
  const cohort = useCohort()

  const softIdRetryExperiment = useExperiment('SoftIdRetryEnabled', false)
  const triggerExperiment = useExperimentTrigger(softIdRetryExperiment)

  const softIdRetryEnabled = softIdRetryExperiment.active && !softIdRetryExperiment.control
  // Create error handler context
  const errorHandlerContext = useMemo<CheckoutErrorContext>(
    () => ({
      dispatch,
      router,
      country,
      flags: {
        enableRedirectionIncompleteConsumerProfile,
        softIdRetryEnabled,
      },
      hasRetriedStartCheckout,
      setHasRetriedStartCheckout,
      logEvent,
      toast,
      t,
      hasPassword,
      unverifiedMobileRoute,
      triggerExperiment,
      profileErrorHandlerContext: {
        enableIncompleteProfileErrorRetry,
      },
    }),
    [
      country,
      enableRedirectionIncompleteConsumerProfile,
      hasPassword,
      hasRetriedStartCheckout,
      setHasRetriedStartCheckout,
      softIdRetryEnabled,
      unverifiedMobileRoute,
      enableIncompleteProfileErrorRetry,
    ]
  )

  // Create error handler
  const errorHandler = createCheckoutErrorHandler(errorHandlerContext)

  const doPostCheckoutMarketingOptInCheck = async (): Promise<void> => {
    if (isPersonalizationEnabled) {
      await dispatch(getMarketingPreferences.initiate(undefined, { forceRefetch: true }))
    }
  }

  const returnApplePayForPayLater = isFetchApplePayRepaymentEnabled && (options?.returnApplePayForPayLater ?? false)

  const doStartCheckout = async (): Promise<
    PromiseSettledResult<{
      preferredCard: PreferredCard | undefined
      applePay: ApplePayState
      preferredBankAccount: PreferredBankAccount | undefined
    }>
  > => {
    const [startResponse] = await Promise.allSettled([
      dispatch(startCheckoutAndSelectPreferredPaymentMethod(isEnabledForMPANApplePayFlow, returnApplePayForPayLater, theme, cohort)),
      doPostCheckoutMarketingOptInCheck(),
    ])
    return startResponse
  }

  const handleStartCheckoutSuccess = (): void => {
    if (isTrustedCheckout || isPersistentAndOptedIn) {
      logLoginAttemptEvent(loginMethod, false)
    }
  }

  const doStartCheckoutAndHandleError = async (): Promise<void> => {
    const startResponse = await doStartCheckout()

    if (startResponse.status === 'fulfilled') {
      handleStartCheckoutSuccess()
      return
    }

    const error = startResponse.reason.data
    let errorCode = startResponse.reason.data?.errorCode

    // Soft Idv retry flow
    if (!hasRetriedStartCheckout && (await shouldRetry(errorCode, errorHandlerContext))) {
      // Log event as expected by the test
      logEvent(TrackingEvent.CHECKOUT_DECLINED, {
        isRecoverableTerminalError: false,
        errorCode,
        identityCheckStatus: IdentityCheckStatus.VERIFIED,
      })
      setHasRetriedStartCheckout(true)
      const retryResponse = await doStartCheckout()
      if (retryResponse.status === 'fulfilled') {
        handleStartCheckoutSuccess()
        return
      }
      // Update error code for final handling
      errorCode = retryResponse.reason.data?.errorCode
    }

    // Use functional error handling
    const result = await errorHandler.handleError(error, errorCode)

    // The new error handler returns the route directly, not an object
    if (result !== undefined && result !== null) {
      router.replace(result)
    }
  }

  return { doStartCheckoutAndHandleError }
}

// Helper function to check if we should retry
const shouldRetry = async (errorCode: ErrorCode, context: { dispatch: ReturnType<typeof useAppDispatch>; country: Country }): Promise<boolean> => {
  const identityCheckErrors = [...errorsEligibleForIdVerification, ...terminalIdvErrors]

  if (!identityCheckErrors.includes(errorCode)) {
    return false
  }

  const needsBiometricVerification = isCountryNeedsBiometricVerification(context.country)
  if (!needsBiometricVerification) {
    return false
  }

  const identitiesResult = await context.dispatch(getIdentities()).unwrap()
  return identitiesResult?.identityCheckStatus === IdentityCheckStatus.VERIFIED
}

export const useIsButtonMerchant = (): boolean => useAppSelector(buttonMerchantSelector)

export const {
  useStartCheckoutQuery,
  useConfirmCheckoutMutation,
  useAbandonCheckoutMutation,
  endpoints: { startCheckout, confirmCheckout, continueCheckout },
} = checkoutService

export const useErrorCode = (): string | undefined => {
  const checkoutError = useAppSelector(checkoutErrorSelector)
  const { error: clError } = useCLRiskDeclineError()
  return checkoutError?.errorCode ?? checkoutError?.error ?? clError?.errorCode
}

export const useIsEnabledPBIAutoPay = (): boolean => useAppSelector(isEnabledPBIAutoPaySelector)

export const useConsumerLendingPromotion = (): ConsumerLendingPromotion | null => useAppSelector(consumerLendingPromotionSelector)

export const useHasZeroAprConsumerLendingPromotion = (): boolean => {
  const promotion = useConsumerLendingPromotion()
  return valueIsNotNullOrUndefined(promotion) && promotion.apr === 0
}

export const useHasViewedZeroAprOffer = (): boolean => {
  const hasZeroAprPromotion = useHasZeroAprConsumerLendingPromotion()
  const pclPaymentOptions = usePclPaymentOptions()

  if (pclPaymentOptions.length > 0) {
    return hasZeroAprPromotion && pclPaymentOptions.some((paymentOption) => paymentOption.apr === 0)
  }

  return hasZeroAprPromotion
}

export const useIsPaymonthlySUPEnabled = (): boolean => {
  const country = useCountry()
  const isPaymonthlySUPEnabled = useFlag<boolean>(Features.PAY_MONTHLY_FOR_SUP_ENABLED, false)

  return country === Country.US && isPaymonthlySUPEnabled
}

export const useIsPaymonthlySUPOrder = (): boolean => {
  const isPayMonthlyOrder = useIsConsumerLendingOrder()
  const isPaymonthlySUPEnabled = useIsPaymonthlySUPEnabled()
  const isSupOrder = useIsSupOrder()
  return isSupOrder && isPaymonthlySUPEnabled && isPayMonthlyOrder
}

export const useShowAffIdvModal = (): boolean => {
  // For environments where we need to bypass IDV (e.g. Sandbox). Should be disabled in prod.
  const shouldBypassIDVerification = useFlag(Features.BYPASS_IDV_FOR_SUP_CPI4)
  const isAFF = useIsAfterpayFinanceFeeEligible()
  const { identityCheckStatuses } = useConsumerSelector()
  return (
    isAFF &&
    !shouldBypassIDVerification &&
    !(identityCheckStatuses?.AFF === IdentityCheckStatus.VERIFIED || identityCheckStatuses?.AFF === IdentityCheckStatus.NOT_REQUIRED)
  )
}

export const useFeatureFlagsState = (): FeatureFlagsState => useAppSelector(featureFlagsSelector)

export const useSessionId = (): SessionIdState => {
  return useAppSelector(sessionIdSelector)
}

export const usePreselectedPaymentType = (): PaymentType | undefined => {
  const offerRanks = useAppSelector(offerRanksSelector)

  return offerRanks[0]
}

export const useIsAppleRetailOrder = (): boolean => {
  const isAppleRetailEnabled = useFlag<boolean>(Features.APPLE_RETAIL_ENABLED, false)
  const checkout = useCheckoutData()
  const orderSource = checkout?.merchant?.source
  const whitelistOrderSources = [OrderSource.APPLE_RETAIL_IN_STORE, OrderSource.APPLE_RETAIL_ONLINE, OrderSource.APPLE_RETAIL_TELESALES]

  return isAppleRetailEnabled && whitelistOrderSources.includes(orderSource as OrderSource)
}

export const useCreditFacilityRedirect = (): (() => void) => {
  const consumerFacilityCohort = useConsumerFacilityCohort()
  const checkoutToken = useCheckoutToken()

  return useCallback(() => {
    if (typeof window === 'undefined') {
      return
    }

    if (consumerFacilityCohort?.getStartedContext !== 'LCCC_EXISTING') {
      return
    }

    const baseUrl = app.CREDIT_APPLICATION_BASE_URL
    if (baseUrl) {
      const url = new URL(baseUrl)
      url.searchParams.set('token', checkoutToken)
      url.searchParams.set('returnUrl', window.location.href)
      browserRedirect(url.toString())
    }
  }, [consumerFacilityCohort?.getStartedContext, checkoutToken])
}

export const useIsAppleRetailOnlineOrder = (): boolean => {
  const isAppleRetailOrder = useIsAppleRetailOrder()
  const checkout = useCheckoutData()
  const orderSource = checkout?.merchant?.source
  return isAppleRetailOrder && orderSource === OrderSource.APPLE_RETAIL_ONLINE
}

export const useIsAppleRetailInstoreOrder = (): boolean => {
  const isAppleRetailOrder = useIsAppleRetailOrder()
  const checkout = useCheckoutData()
  const orderSource = checkout?.merchant?.source
  return isAppleRetailOrder && orderSource === OrderSource.APPLE_RETAIL_IN_STORE
}

export const useIsAppleRetailTelesalesOrder = (): boolean => {
  const isAppleRetailOrder = useIsAppleRetailOrder()
  const checkout = useCheckoutData()
  const orderSource = checkout?.merchant?.source
  return isAppleRetailOrder && orderSource === OrderSource.APPLE_RETAIL_TELESALES
}

export const useIntegrationNextRoute = (): Route => useAppSelector(integrationNextRouteSelector)

export const useOrderSource = (): OrderSource | undefined => {
  const checkout = useCheckoutData()
  return checkout?.merchant?.source
}

// Items Vault Hooks
export const useItemsDataVault = (): Record<string, EnrichedItem> => useAppSelector(itemsVaultSelector).data
export const useItemById = (id: string): EnrichedItem | undefined => useAppSelector(itemByIdSelector(id))
export const useItemsIds = (): string[] => useAppSelector(itemsVaultSelector).ids
export const useItemsCount = (): number => useAppSelector(itemsVaultSelector).ids.length

const mapOrderSourceToCheckoutChannel = (orderSource: OrderSource | undefined, isAppleRetailOrder: boolean): 'ONLINE' | 'INSTORE' | 'TELESALES' | undefined => {
  if (!isAppleRetailOrder || orderSource == null) {
    return undefined
  }

  switch (orderSource) {
    case OrderSource.APPLE_RETAIL_ONLINE:
      return 'ONLINE'
    case OrderSource.APPLE_RETAIL_IN_STORE:
      return 'INSTORE'
    case OrderSource.APPLE_RETAIL_TELESALES:
      return 'TELESALES'
    default:
      return undefined
  }
}

export const useAppleRetailTrackingProps = (): AppleRetailTrackingProps => {
  const isAppleRetailOrder = useIsAppleRetailOrder()
  const checkout = useCheckoutData()
  const orderSource = checkout?.merchant?.source
  const checkoutChannel = mapOrderSourceToCheckoutChannel(orderSource, isAppleRetailOrder)
  const currency = checkout?.amount?.currency

  // TODO: consolidate with useSummaryDetailsViewModel to dedupe logic
  const items = useItemsDataVault()
  const itemsData = useMemo(() => Object.values(items ?? {}), [items])

  const derived = useMemo<AppleRetailTrackingProps>(() => {
    if (!isAppleRetailOrder) {
      return {}
    }

    const resolvedCurrency = currency ?? itemsData.find((i) => i?.price?.currency != null)?.price?.currency ?? 'AUD'

    const loanItems = itemsData.filter((item) => item.paymentType === PaymentType.PCL && isUndefinedOrEmpty(item.parentId))

    const payInFullItems = itemsData.filter((item) => item.paymentType === PaymentType.PBI && item.numberOfInstallments === 1)

    const downPaymentAmount = loanItems.reduce((acc: Money, item: EnrichedItem): Money => {
      const sum = addMoneys({ moneys: [acc, item.downPaymentAmount ?? money('0', resolvedCurrency)] })
      return money(sum, resolvedCurrency)
    }, money('0', resolvedCurrency))

    const interestAmount = loanItems.reduce((acc: Money, item: EnrichedItem): Money => {
      const sum = addMoneys({ moneys: [acc, item.interestAmount ?? money('0', resolvedCurrency)] })
      return money(sum, resolvedCurrency)
    }, money('0', resolvedCurrency))

    const tradeInItems = itemsData.filter((item) => !isUndefinedOrEmpty(item.parentId))
    const tradeinAmount = tradeInItems.reduce((acc: Money, item: EnrichedItem): Money => {
      const sum = addMoneys({ moneys: [acc, item.price ?? money('0', resolvedCurrency)] })
      return money(sum, resolvedCurrency)
    }, money('0', resolvedCurrency))

    // Do we have more than one shipping item?
    const shippingItem = itemsData.find((item) => item.type === ItemType.SHIPPING)
    const shippingFee = shippingItem?.price ?? money('0', resolvedCurrency)

    return {
      checkoutChannel,
      loanItemIds: loanItems.map((i) => i.id),
      payinfullItemIds: payInFullItems.map((i) => i.id),
      isChargeatship: checkoutChannel === 'ONLINE' || checkoutChannel === 'TELESALES',
      downPaymentAmount,
      interestAmount,
      tradeinAmount,
      shippingFee,
    }
  }, [checkoutChannel, currency, isAppleRetailOrder, itemsData])

  return derived
}
